"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  CheckCircle,
  X,
  Sparkles,
} from "lucide-react";
import useApi from "@/hooks/useApi";
import { PricingCalculator } from "@/components/PricingCalculator";
import API_ENDPOINTS from "@/constants/endpoints";
import { PricingPlan } from "@/services/payment";
import { toast } from "sonner";
import Link from "next/link";

export default function Pricing() {
  const [pricingData, setPricingData] = useState<PricingPlan[]>([]);
  const { fetchData } = useApi();

  useEffect(() => {
    fetchData(API_ENDPOINTS.PAYMENT.PRICING, "GET", null, {}, false, false).then((res) => {
      if (res) setPricingData(res);
    });
  }, []);



  const features = [
    {
      category: "Detection Features",
      items: [
        {
          name: "Basic plagiarism detection",
          free: true,
          pro: true,
          enterprise: true,
        },
        {
          name: "Advanced AI detection",
          free: false,
          pro: true,
          enterprise: true,
        },
        {
          name: "Grammar & style checking",
          free: false,
          pro: true,
          enterprise: true,
        },
        {
          name: "Citation detection",
          free: false,
          pro: true,
          enterprise: true,
        },
        {
          name: "Paraphrasing detection",
          free: false,
          pro: true,
          enterprise: true,
        },
      ],
    },
    {
      category: "Usage Limits",
      items: [
        { name: "File uploads", free: "✓", pro: "✓", enterprise: "✓" },
        { name: "Bulk checking", free: false, pro: true, enterprise: true },
      ],
    },
    {
      category: "Reports & Export",
      items: [
        {
          name: "Basic similarity report",
          free: true,
          pro: true,
          enterprise: true,
        },
        {
          name: "Detailed source citations",
          free: false,
          pro: true,
          enterprise: true,
        },
        { name: "PDF & Word export", free: false, pro: true, enterprise: true },
        {
          name: "White-label reports",
          free: false,
          pro: false,
          enterprise: true,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100 px-3 py-1">
            <Sparkles className="w-4 h-4 mr-2 inline" />
            Simple, Transparent Pricing
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Pay for what you need
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Scale your plagiarism detection seamlessly. No hidden fees, cancel
            anytime.
          </p>
        </div>

        {/* Pricing Calculator Section */}
        <div className="max-w-5xl mx-auto mb-20 relative">
          {/* Main Calculator Card */}
          <PricingCalculator pricingData={pricingData} />

          {/* Complementary Free Plan */}
          {/* <div className="mt-8 text-center bg-white border border-gray-200 rounded-2xl p-6 shadow-sm mx-auto max-w-3xl flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="text-left flex-1 text-center sm:text-left">
              <h3 className="text-lg font-bold text-gray-900 flex items-center justify-center sm:justify-start gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Just need occasionally?
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Try our basic tools completely free. 5 checks/mo (up to 1,000
                words).
              </p>
            </div>
            <Button
              variant="outline"
              className="border-gray-300 hover:bg-gray-50 flex-shrink-0"
            >
              Create Free Account
            </Button>
          </div> */}
        </div>

        {/* Feature Comparison Table */}
        <Card className="p-8 mb-16 rounded-3xl border-gray-100 shadow-sm overflow-hidden bg-white">
          <div className="text-center mb-10 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Detailed Features
            </h2>
            <p className="text-gray-500">
              Every paid plan includes full access to our premium suite.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="py-5 px-6 font-semibold text-gray-900 w-2/5">
                    Feature Overview
                  </th>
                  <th className="py-5 px-6 font-semibold text-gray-900 text-center w-1/5 border-l border-gray-100">
                    Free
                  </th>
                  <th className="py-5 px-6 font-bold text-blue-600 text-center w-1/5 border-l border-gray-100 bg-blue-50/30">
                    Pro (Volume Base)
                  </th>
                  <th className="py-5 px-6 font-semibold text-gray-900 text-center w-1/5 border-l border-gray-100">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody>
                {features.map((category, categoryIndex) => (
                  <React.Fragment key={categoryIndex}>
                    <tr className="bg-gray-50 border-y border-gray-200">
                      <td
                        colSpan={4}
                        className="py-3 px-6 font-bold text-gray-900 text-sm uppercase tracking-wider"
                      >
                        {category.category}
                      </td>
                    </tr>
                    {category.items.map((item, itemIndex) => (
                      <tr
                        key={itemIndex}
                        className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6 text-gray-700 font-medium">
                          {item.name}
                        </td>
                        <td className="py-4 px-6 text-center border-l border-gray-100">
                          {typeof item.free === "boolean" ? (
                            item.free ? (
                              <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )
                          ) : (
                            <span className="text-sm font-semibold text-gray-600">
                              {item.free}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-center border-l border-gray-100 bg-blue-50/10">
                          {typeof item.pro === "boolean" ? (
                            item.pro ? (
                              <CheckCircle className="h-5 w-5 text-blue-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )
                          ) : (
                            <span className="text-sm font-semibold text-blue-600">
                              {item.pro}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-center border-l border-gray-100">
                          {typeof item.enterprise === "boolean" ? (
                            item.enterprise ? (
                              <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )
                          ) : (
                            <span className="text-sm font-semibold text-gray-600">
                              {item.enterprise}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* FAQ Section */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Questions about pricing?
          </h2>
          <p className="text-gray-600 mb-8">
            Our team is here to help you choose the right plan for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contact">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              Contact Support
            </Button>
            </Link>
            {/* <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              Schedule Demo
            </Button> */}
          </div>
        </div>
      </div>
    </div>
  );
}
